// Jelly soft-body outlines (visuals §4; backlog R1-2, R2-6): per-entity vertex radius offsets for
// cells, viruses and ejected mass, so outlines dent where they touch pellets, other bodies and the
// map wall, bulge a little beside each dent, wobble slightly at rest and spring back with a small
// overshoot when a contact goes away.
//
// Model (our own; tuned against the measured agar behaviour in visuals §4.1 and the acceptance
// tests in test/render/jelly.test.ts). Per entity id: `off[n]`, `vel[n]`, `noise[n]`.
// - Contacts are worked out once per frame from the frame's bodies (they do not move between
//   sub-steps): for each vertex rest point p_i = c + r·u_i inside a body B, the target dent is
//   −min(K_BODY · (B.r − |p_i − B.c|), DENT_MAX); beyond a map wall, −min(out, WALL_MAX).
// - Bulges: vertices with no contact of their own get an outward target from the contacts within
//   ±BULGE_SPAN of them (a local area-preservation term: what a dent pushes in, its neighbours
//   push out), capped at BULGE_MAX.
// - Idle noise: a bounded, slightly mean-reverting random walk per vertex, smoothed along the ring, amplitude
//   A = clamp(0.015·r, 0.6, 2) u (viruses 1.5 u); deterministic from (id, vertex, step).
// - Dynamics on a fixed 60 Hz sub-step (≤ 4 per frame): a damped spring toward the target plus
//   neighbour coupling, `vel = vel·DAMP + (target − off)·STIFF + COUPLE·Δoff; off += vel`, which
//   recovers a released dent in ~2 steps with a ~25 % overshoot and settles in ~150 ms.
//
// Pure maths on typed arrays (no DOM); the painters turn offsets into polygons (outline.ts).

import { hashCombine } from '@ballio/shared';
import type { ViewRect } from './cull.js';

const TAU = Math.PI * 2;

export const JELLY = Object.freeze({
  /** Fixed sub-step (60 Hz) and the most sub-steps run in one frame. */
  stepMs: 1000 / 60,
  maxStepsPerFrame: 4,
  /** Dent per unit of penetration of a vertex into a body, and its bound. */
  kBody: 0.3,
  dentMax: 15,
  /** Pull-in bound beyond the map wall. */
  wallMax: 15,
  stiff: 0.5,
  damp: 0.5,
  couple: 0.1,
  /** Outward push beside a dent: × the mean dent within ±bulgeSpanRad, capped. */
  bulge: 1.6,
  bulgeSpanRad: (24 * Math.PI) / 180,
  bulgeMax: 8,
  /** Idle noise amplitude A = clamp(noisePerRadius · r, noiseMin, noiseMax). */
  noisePerRadius: 0.015,
  noiseMin: 0.6,
  noiseMax: 2,
  /** Random-walk step as a share of A, and the per-step pull back toward 0 (bounded drift). */
  noiseStep: 0.25,
  noiseRevert: 0.015,
  /** Bodies are tested when their centre is within r + B.r + this. */
  contactMargin: 15,
  maxContacts: 48,
  /** Skip a body bigger than this × r whose centre is inside the cell (eating / being eaten). */
  skipBiggerFactor: 1.15,
  /** Uniform grid bucket size for the frame's bodies. */
  bucketU: 128,
  /** Bodies above this radius are kept in a separate list (tested against every entity). */
  bigBodyR: 64,
  /** Per-entity state is dropped after this long unseen. */
  forgetMs: 1000,
  /** Vertex count rule (visuals §3): N = clamp(round(r_px), min, max). */
  minVertices: 16,
  maxVertices: 160,
  /** Below this on-screen radius (CSS px) bodies are drawn as plain two-circle bands. */
  minRadiusPx: 8,
});

/** Vertex count of a jelly polygon for an on-screen radius (CSS px): clamp(round(rPx), 16, 160). */
export function jellyVertexCount(rPx: number): number {
  const n = Math.round(Number.isFinite(rPx) ? rPx : 0);
  return n < JELLY.minVertices ? JELLY.minVertices : n > JELLY.maxVertices ? JELLY.maxVertices : n;
}

/** Idle noise amplitude in world units for a radius: clamp(0.015·r, 0.6, 2). */
export function jellyNoiseAmplitude(r: number): number {
  const a = JELLY.noisePerRadius * r;
  return a < JELLY.noiseMin ? JELLY.noiseMin : a > JELLY.noiseMax ? JELLY.noiseMax : a;
}

/** A body the jelly contacts can be against (centre and drawn radius). */
export interface JellyBody {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

/** Per-entity options for `offsets`. */
export interface JellyOptions {
  /** Idle noise amplitude in world units (default `jellyNoiseAmplitude(r)`; 0 = none). */
  readonly noise?: number;
}

class JellyState {
  n = 0;
  steps = 0;
  seenMs = 0;
  off: Float64Array;
  vel: Float64Array;
  noise: Float64Array;
  constructor(cap: number) {
    this.off = new Float64Array(cap);
    this.vel = new Float64Array(cap);
    this.noise = new Float64Array(cap);
  }
}

/** A deterministic value in [0, 1) from (id, vertex, step). */
function rnd(id: number, i: number, step: number): number {
  return hashCombine(hashCombine(id >>> 0, i), step) / 0x100000000;
}

/** Linear resample of a closed ring of `from` values into `to` values, in place via `tmp`. */
function resampleRing(a: Float64Array, from: number, to: number, tmp: Float64Array): void {
  if (from <= 0) {
    a.fill(0, 0, to);
    return;
  }
  for (let j = 0; j < to; j++) {
    const pos = (j * from) / to;
    const i0 = Math.floor(pos);
    const t = pos - i0;
    const v0 = a[i0 % from] ?? 0;
    const v1 = a[(i0 + 1) % from] ?? 0;
    tmp[j] = v0 + (v1 - v0) * t;
  }
  for (let j = 0; j < to; j++) a[j] = tmp[j] ?? 0;
}

const TABLE_BITS = 12;
const TABLE_SIZE = 1 << TABLE_BITS;
const TABLE_MASK = TABLE_SIZE - 1;

function bucketKey(bx: number, by: number): number {
  return (Math.imul(bx, 73856093) ^ Math.imul(by, 19349663)) & TABLE_MASK;
}

/**
 * Jelly state for every drawn soft body. Per frame: `beginFrame` with the frame's bodies and the
 * map, then `offsets(...)` for each drawn entity (advances it by the frame's sub-steps), then
 * `endFrame` (forgets entities unseen for a second).
 */
export class JellyField {
  private readonly states = new Map<number, JellyState>();
  private readonly pool: JellyState[] = [];
  private accMs = 0;
  private lastNowMs = Number.NaN;
  private stepsThisFrame = 0;
  private nowMs = 0;
  private lastSweepMs = 0;
  private map: ViewRect | null = null;

  // Frame bodies (struct of arrays) and their grid.
  private count = 0;
  private bx = new Float64Array(256);
  private by = new Float64Array(256);
  private br = new Float64Array(256);
  private bid = new Float64Array(256);
  private next = new Int32Array(256);
  private readonly heads = new Int32Array(TABLE_SIZE).fill(-1);
  private readonly usedKeys: number[] = [];
  private big: number[] = [];

  // Scratch.
  private readonly contact = new Float64Array(JELLY.maxVertices + 1);
  private readonly target = new Float64Array(JELLY.maxVertices + 1);
  private readonly prefix = new Float64Array(2 * JELLY.maxVertices + 2);
  private readonly tmp = new Float64Array(JELLY.maxVertices + 1);
  private readonly candIdx = new Int32Array(1024);
  private readonly candDist = new Float64Array(1024);
  private readonly cos = new Float64Array(JELLY.maxVertices + 1);
  private readonly sin = new Float64Array(JELLY.maxVertices + 1);
  private ringN = 0;

  /** Entities with jelly state (tests, stats). */
  get size(): number {
    return this.states.size;
  }

  /** Sub-steps every entity advances this frame (set by `beginFrame`). */
  get steps(): number {
    return this.stepsThisFrame;
  }

  /**
   * Starts a frame at `nowMs`: works out the sub-steps due (fixed 60 Hz, ≤ 4) and indexes the
   * frame's `bodies` (pellets, ejected, cells, viruses; drawn radii) for the contact queries.
   * `steps` forces a sub-step count (tests).
   */
  beginFrame(
    nowMs: number,
    bodies: readonly JellyBody[],
    bodyCount: number,
    map: ViewRect | null,
    steps?: number,
  ): void {
    this.nowMs = nowMs;
    this.map = map;
    if (steps !== undefined) {
      this.stepsThisFrame = Math.max(0, Math.floor(steps));
    } else {
      const dt = Number.isFinite(this.lastNowMs) ? nowMs - this.lastNowMs : JELLY.stepMs;
      this.accMs += dt > 0 && dt < 1000 ? dt : JELLY.stepMs;
      let n = Math.floor(this.accMs / JELLY.stepMs + 1e-6);
      this.accMs -= n * JELLY.stepMs;
      if (n > JELLY.maxStepsPerFrame) {
        n = JELLY.maxStepsPerFrame;
        this.accMs = 0;
      }
      this.stepsThisFrame = n;
    }
    this.lastNowMs = nowMs;
    this.index(bodies, bodyCount);
  }

  /** Forgets entities not drawn for `JELLY.forgetMs` (call once per frame). */
  endFrame(): void {
    const now = this.nowMs;
    if (now - this.lastSweepMs < 500) return;
    this.lastSweepMs = now;
    for (const [id, st] of this.states) {
      if (now - st.seenMs > JELLY.forgetMs) {
        this.states.delete(id);
        this.pool.push(st);
      }
    }
  }

  /** Drops all state (style change, tests). */
  clear(): void {
    for (const st of this.states.values()) this.pool.push(st);
    this.states.clear();
  }

  /**
   * The vertex offsets (world units, `n` of them from angle 0) of entity `id` drawn at
   * `(x, y, r)`, advanced by this frame's sub-steps. The array is the entity's own state: read
   * it before the next `offsets` call for the same id and do not write it.
   */
  offsets(
    id: number,
    x: number,
    y: number,
    r: number,
    n: number,
    opts: JellyOptions = {},
  ): Float64Array {
    const nn = Math.max(3, Math.min(JELLY.maxVertices, Math.floor(n)));
    const amp = Math.max(0, opts.noise ?? jellyNoiseAmplitude(r));
    let st = this.states.get(id);
    if (!st) {
      st = this.pool.pop() ?? new JellyState(JELLY.maxVertices + 1);
      st.n = nn;
      st.steps = 0;
      for (let i = 0; i < nn; i++) {
        const v = (rnd(id, i, 0x7fffffff) - 0.5) * 2 * amp;
        st.noise[i] = v;
        st.vel[i] = 0;
      }
      this.smoothNoise(st, nn);
      this.states.set(id, st);
      this.computeTargets(id, x, y, r, nn);
      for (let i = 0; i < nn; i++) st.off[i] = (this.target[i] ?? 0) + (st.noise[i] ?? 0);
      st.seenMs = this.nowMs;
      return st.off;
    }
    if (st.n !== nn) {
      resampleRing(st.off, st.n, nn, this.tmp);
      resampleRing(st.vel, st.n, nn, this.tmp);
      resampleRing(st.noise, st.n, nn, this.tmp);
      st.n = nn;
    }
    st.seenMs = this.nowMs;
    const steps = this.stepsThisFrame;
    if (steps <= 0) return st.off;
    this.computeTargets(id, x, y, r, nn);
    for (let s = 0; s < steps; s++) this.step(id, st, nn, amp);
    return st.off;
  }

  /** The contact (dent + bulge) targets of an entity for this frame into `this.target`. */
  computeTargets(id: number, x: number, y: number, r: number, n: number): Float64Array {
    const c = this.contact;
    const out = this.target;
    for (let i = 0; i < n; i++) c[i] = 0;
    this.ring(n);
    if (r > 0) {
      this.bodyContacts(id, x, y, r, n);
      this.wallContacts(x, y, r, n);
    }
    this.bulges(n);
    return out;
  }

  // -------------------------------------------------------------------------------------------

  private step(id: number, st: JellyState, n: number, amp: number): void {
    const off = st.off;
    const vel = st.vel;
    const noise = st.noise;
    const step = ++st.steps;
    if (amp > 0) {
      const d = JELLY.noiseStep * amp;
      const keep = 1 - JELLY.noiseRevert;
      for (let i = 0; i < n; i++) {
        let v = (noise[i] ?? 0) * keep + (rnd(id, i, step) - 0.5) * 2 * d;
        if (v > amp) v = amp;
        else if (v < -amp) v = -amp;
        noise[i] = v;
      }
      this.smoothNoise(st, n);
    } else {
      for (let i = 0; i < n; i++) noise[i] = 0;
    }
    const target = this.target;
    const { stiff, damp, couple } = JELLY;
    for (let i = 0; i < n; i++) {
      const o = off[i] ?? 0;
      const prev = off[i === 0 ? n - 1 : i - 1] ?? 0;
      const next = off[i === n - 1 ? 0 : i + 1] ?? 0;
      const t = (target[i] ?? 0) + (noise[i] ?? 0);
      vel[i] = (vel[i] ?? 0) * damp + (t - o) * stiff + couple * (prev + next - 2 * o);
    }
    for (let i = 0; i < n; i++) off[i] = (off[i] ?? 0) + (vel[i] ?? 0);
  }

  private smoothNoise(st: JellyState, n: number): void {
    const a = st.noise;
    const tmp = this.tmp;
    for (let i = 0; i < n; i++) {
      const prev = a[i === 0 ? n - 1 : i - 1] ?? 0;
      const next = a[i === n - 1 ? 0 : i + 1] ?? 0;
      tmp[i] = 0.25 * prev + 0.5 * (a[i] ?? 0) + 0.25 * next;
    }
    for (let i = 0; i < n; i++) a[i] = tmp[i] ?? 0;
  }

  private ring(n: number): void {
    if (this.ringN === n) return;
    this.ringN = n;
    for (let i = 0; i < n; i++) {
      const a = (i * TAU) / n;
      this.cos[i] = Math.cos(a);
      this.sin[i] = Math.sin(a);
    }
  }

  private bodyContacts(id: number, x: number, y: number, r: number, n: number): void {
    const reach = r + JELLY.contactMargin;
    let nc = 0;
    const maxCand = this.candIdx.length;
    const consider = (k: number): void => {
      if ((this.bid[k] ?? -1) === id) return;
      const br = this.br[k] ?? 0;
      if (!(br > 0)) return;
      const dx = (this.bx[k] ?? 0) - x;
      const dy = (this.by[k] ?? 0) - y;
      const lim = reach + br;
      const d2 = dx * dx + dy * dy;
      if (d2 >= lim * lim) return;
      const d = Math.sqrt(d2);
      if (br > JELLY.skipBiggerFactor * r && d < r) return; // eating / being eaten
      if (Math.abs(d - r) >= br || d < 1e-6) return; // no rest point inside B
      if (nc < maxCand) {
        this.candIdx[nc] = k;
        this.candDist[nc] = Math.abs(d - r) - br;
        nc++;
      }
    };
    // Grid buckets around the circle (small bodies are indexed by centre).
    const pad = reach + JELLY.bigBodyR;
    const s = JELLY.bucketU;
    const x0 = Math.floor((x - pad) / s);
    const x1 = Math.floor((x + pad) / s);
    const y0 = Math.floor((y - pad) / s);
    const y1 = Math.floor((y + pad) / s);
    const span = (x1 - x0 + 1) * (y1 - y0 + 1);
    if (span <= TABLE_SIZE / 4) {
      for (let gy = y0; gy <= y1; gy++) {
        for (let gx = x0; gx <= x1; gx++) {
          for (let k = this.heads[bucketKey(gx, gy)] ?? -1; k >= 0; k = this.next[k] ?? -1) {
            const kx = Math.floor((this.bx[k] ?? 0) / s);
            const ky = Math.floor((this.by[k] ?? 0) / s);
            if (kx === gx && ky === gy) consider(k);
          }
        }
      }
    } else {
      for (let k = 0; k < this.count; k++) if ((this.br[k] ?? 0) <= JELLY.bigBodyR) consider(k);
    }
    for (let j = 0; j < this.big.length; j++) consider(this.big[j] ?? 0);
    if (nc === 0) return;
    if (nc > JELLY.maxContacts) {
      // Nearest (deepest) first: a small selection sort of the first maxContacts.
      for (let a = 0; a < JELLY.maxContacts; a++) {
        let best = a;
        for (let b = a + 1; b < nc; b++) {
          if ((this.candDist[b] ?? 0) < (this.candDist[best] ?? 0)) best = b;
        }
        if (best !== a) {
          const ti = this.candIdx[a] ?? 0;
          const td = this.candDist[a] ?? 0;
          this.candIdx[a] = this.candIdx[best] ?? 0;
          this.candDist[a] = this.candDist[best] ?? 0;
          this.candIdx[best] = ti;
          this.candDist[best] = td;
        }
      }
      nc = JELLY.maxContacts;
    }
    const c = this.contact;
    const cos = this.cos;
    const sin = this.sin;
    const stepA = TAU / n;
    const { kBody, dentMax } = JELLY;
    for (let j = 0; j < nc; j++) {
      const k = this.candIdx[j] ?? 0;
      const bxk = this.bx[k] ?? 0;
      const byk = this.by[k] ?? 0;
      const br = this.br[k] ?? 0;
      const dx = bxk - x;
      const dy = byk - y;
      const d = Math.hypot(dx, dy);
      // Rest points within angle ±phi of the body direction are inside it.
      let cosPhi = (r * r + d * d - br * br) / (2 * r * d);
      if (cosPhi > 1) cosPhi = 1;
      const phi = cosPhi < -1 ? Math.PI : Math.acos(cosPhi);
      const theta = Math.atan2(dy, dx);
      const i0 = Math.ceil((theta - phi) / stepA);
      const i1 = Math.floor((theta + phi) / stepA);
      const last = Math.min(i1, i0 + n - 1);
      for (let ii = i0; ii <= last; ii++) {
        const i = ((ii % n) + n) % n;
        const px = x + r * (cos[i] ?? 0) - bxk;
        const py = y + r * (sin[i] ?? 0) - byk;
        const pen = br - Math.sqrt(px * px + py * py);
        if (pen <= 0) continue;
        const dent = -(kBody * pen < dentMax ? kBody * pen : dentMax);
        if (dent < (c[i] ?? 0)) c[i] = dent;
      }
    }
  }

  private wallContacts(x: number, y: number, r: number, n: number): void {
    const m = this.map;
    if (!m) return;
    const right = x + r > m.maxX;
    const left = x - r < m.minX;
    const bottom = y + r > m.maxY;
    const top = y - r < m.minY;
    if (!right && !left && !bottom && !top) return;
    const c = this.contact;
    const cos = this.cos;
    const sin = this.sin;
    const wallMax = JELLY.wallMax;
    for (let i = 0; i < n; i++) {
      const px = x + r * (cos[i] ?? 0);
      const py = y + r * (sin[i] ?? 0);
      let out = 0;
      if (right && px - m.maxX > out) out = px - m.maxX;
      if (left && m.minX - px > out) out = m.minX - px;
      if (bottom && py - m.maxY > out) out = py - m.maxY;
      if (top && m.minY - py > out) out = m.minY - py;
      if (out > 0) {
        const dent = -(out < wallMax ? out : wallMax);
        if (dent < (c[i] ?? 0)) c[i] = dent;
      }
    }
  }

  /** target = contact + bulge (outward push on contact-free vertices beside a dent). */
  private bulges(n: number): void {
    const c = this.contact;
    const out = this.target;
    let any = false;
    for (let i = 0; i < n; i++) {
      if ((c[i] ?? 0) < 0) {
        any = true;
        break;
      }
    }
    if (!any) {
      for (let i = 0; i < n; i++) out[i] = 0;
      return;
    }
    const h = Math.max(1, Math.round((JELLY.bulgeSpanRad * n) / TAU));
    // Circular prefix sums of the contacts.
    const p = this.prefix;
    p[0] = 0;
    for (let i = 0; i < 2 * n; i++) p[i + 1] = (p[i] ?? 0) + (c[i % n] ?? 0);
    const w = 2 * h + 1;
    const { bulge, bulgeMax } = JELLY;
    for (let i = 0; i < n; i++) {
      const ci = c[i] ?? 0;
      if (ci < 0) {
        out[i] = ci;
        continue;
      }
      // Window [i − h, i + h] mapped into [n + i − h, n + i + h] of the doubled ring.
      const lo = n + i - h;
      const hi = n + i + h;
      const a = Math.max(0, lo);
      const b = Math.min(2 * n - 1, hi);
      const sum = (p[b + 1] ?? 0) - (p[a] ?? 0);
      const push = (-sum / w) * bulge;
      out[i] = push < bulgeMax ? push : bulgeMax;
    }
  }

  private index(bodies: readonly JellyBody[], bodyCount: number): void {
    for (let j = 0; j < this.usedKeys.length; j++) this.heads[this.usedKeys[j] ?? 0] = -1;
    this.usedKeys.length = 0;
    this.big.length = 0;
    const count = Math.min(bodyCount, bodies.length);
    if (this.bx.length < count) {
      const cap = Math.max(count, this.bx.length * 2);
      this.bx = new Float64Array(cap);
      this.by = new Float64Array(cap);
      this.br = new Float64Array(cap);
      this.bid = new Float64Array(cap);
      this.next = new Int32Array(cap);
    }
    const s = JELLY.bucketU;
    for (let k = 0; k < count; k++) {
      const b = bodies[k] as JellyBody;
      this.bx[k] = b.x;
      this.by[k] = b.y;
      this.br[k] = b.r;
      this.bid[k] = b.id;
      if (b.r > JELLY.bigBodyR) {
        this.big.push(k);
        this.next[k] = -1;
        continue;
      }
      const key = bucketKey(Math.floor(b.x / s), Math.floor(b.y / s));
      const head = this.heads[key] ?? -1;
      if (head < 0) this.usedKeys.push(key);
      this.next[k] = head;
      this.heads[key] = k;
    }
    this.count = count;
  }
}
